/**
 * Analytics Constants
 * Event categories, thresholds, and configuration values
 */

import type { WebVitalMetric, PerformanceRating, EventCategory } from '../types'

// ============================================
// Web Vitals Thresholds
// ============================================

/** Web Vitals thresholds for ratings (in ms, except CLS which is unitless) */
export const WEB_VITALS_THRESHOLDS: Record<WebVitalMetric, { good: number; poor: number }> = {
  LCP: { good: 2500, poor: 4000 },   // Largest Contentful Paint
  FID: { good: 100, poor: 300 },     // First Input Delay
  CLS: { good: 0.1, poor: 0.25 },    // Cumulative Layout Shift
  TTFB: { good: 800, poor: 1800 },   // Time to First Byte
  INP: { good: 200, poor: 500 },     // Interaction to Next Paint
  FCP: { good: 1800, poor: 3000 },   // First Contentful Paint
}

/** Human-readable names for Web Vitals */
export const WEB_VITALS_NAMES: Record<WebVitalMetric, string> = {
  LCP: 'Largest Contentful Paint',
  FID: 'First Input Delay',
  CLS: 'Cumulative Layout Shift',
  TTFB: 'Time to First Byte',
  INP: 'Interaction to Next Paint',
  FCP: 'First Contentful Paint',
}

/**
 * Get rating for a Web Vital metric value
 */
export function getWebVitalRating(metric: WebVitalMetric, value: number): PerformanceRating {
  const thresholds = WEB_VITALS_THRESHOLDS[metric]
  if (value <= thresholds.good) return 'good'
  if (value <= thresholds.poor) return 'needs-improvement'
  return 'poor'
}

/**
 * Format Web Vital value for display
 */
export function formatWebVitalValue(metric: WebVitalMetric, value: number): string {
  if (metric === 'CLS') {
    return value.toFixed(3)
  }
  return `${Math.round(value)}ms`
}

// ============================================
// Event Category Labels
// ============================================

/** Human-readable labels for event categories */
export const EVENT_CATEGORY_LABELS: Record<EventCategory, string> = {
  navigation: 'Navigation',
  engagement: 'Engagement',
  conversion: 'Conversion',
  feature: 'Feature Usage',
  form: 'Form',
  error: 'Error',
  performance: 'Performance',
  search: 'Search',
  filter: 'Filter',
  auth: 'Authentication',
  order: 'Order',
  contract: 'Contract',
  shipment: 'Shipment',
  asset: 'Asset',
  finance: 'Finance',
  fleet: 'Fleet',
  user: 'User Management',
}

// ============================================
// Redux Action Patterns
// ============================================

/** Patterns for Redux actions to auto-track */
export const REDUX_ACTION_PATTERNS = {
  /** Match all fetch/load actions */
  FETCH: /fetch|load|get/i,
  /** Match all create/add actions */
  CREATE: /create|add|post/i,
  /** Match all update/edit actions */
  UPDATE: /update|edit|patch|set/i,
  /** Match all delete/remove actions */
  DELETE: /delete|remove/i,
  /** Match all success actions */
  SUCCESS: /Success$/,
  /** Match all failure/error actions */
  FAILURE: /Failure$|Error$/,
  /** Match user-initiated actions */
  USER_ACTION: /click|submit|select|toggle|open|close/i,
}

/** Actions to always exclude from tracking */
export const EXCLUDED_ACTIONS = [
  '@@redux/INIT',
  '@@redux/PROBE_UNKNOWN_ACTION',
  'persist/PERSIST',
  'persist/REHYDRATE',
  'analytics/trackPageView',
  'analytics/trackEvent',
  'analytics/trackError',
  'analytics/trackPerformance',
  'analytics/identify',
]

// ============================================
// Timing Constants
// ============================================

/** Session timeout in milliseconds (30 minutes) */
export const SESSION_TIMEOUT_MS = 30 * 60 * 1000

/** Default batch flush interval (5 seconds) */
export const DEFAULT_FLUSH_INTERVAL_MS = 5000

/** Default batch size */
export const DEFAULT_BATCH_SIZE = 10

/** Max stack trace length */
export const MAX_STACK_TRACE_LENGTH = 2000

// ============================================
// Storage Keys
// ============================================

/** Storage keys for analytics */
export const STORAGE_KEYS = {
  SESSION_ID: 'spcc-analytics-session-id',
  SESSION_START: 'spcc-analytics-session-start',
  CONSENT: 'spcc-consent',
  CONSENT_VERSION: 'spcc-consent-version',
  USER_ID: 'spcc-analytics-user-id',
}

// ============================================
// Error Severity Weights
// ============================================

/** Numeric weights for error severity (for aggregation) */
export const ERROR_SEVERITY_WEIGHTS = {
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
}
